import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
	Box,
	Flex,
	PrimaryButton,
	Text,
	Loader,
	CheckmarkIllustration,
} from '@stashinvest/ui';
import InfoToggle from 'stash/components/global/InfoToggle';
import { selectAccount } from 'stash/selectors';
import {
	postAccount,
	postAccountSubmit,
	getEsigAgreements,
	openDialog,
} from 'stash/actions';
import { PERSONAL_BROKERAGE } from 'stash/constants/accountTypes';
import { SignUpStep } from 'stash/components/sign-up/SignUpScreenLayout';
import { ESigDocument } from '../SignUpESign';

const contactUsMsg = (accountType) => {
	return `Sorry...we had an issue creating your ${accountType} account. Please email customer support at support@stash.com.`;
};

export const mapStateToProps = (state, ownProps) => ({
	investAccountId: state.entities.account_by_type.PERSONAL_BROKERAGE,
	investAccount: selectAccount(state, PERSONAL_BROKERAGE),
	agreementsData: state.entities.agreements,
	profile: state.entities.profile,
	userId: state.entities.user.uuid,
	platform_tier: state.entities.user.platform_tier,
});

export const mapDispatchToProps = (dispatch, ownProps) => ({
	submitAccount: (data) => {
		return dispatch(postAccount(data));
	},
	submitAccountToApex: (accountId) => {
		return dispatch(postAccountSubmit(accountId, { e_signature_agreed: true }));
	},
	fetchMultiEsigAgreements: (accountTypes) => {
		return dispatch(getEsigAgreements(accountTypes));
	},
	showDialog: (msg, primaryCta, secondaryCta) => {
		return dispatch(openDialog(msg, primaryCta, secondaryCta)).catch(() => {});
	},
});

export const mergeProps = (stateProps, dispatchProps, ownProps) => {
	return {
		...stateProps,
		...dispatchProps,
		...ownProps,
		onSubmit: async () => {
			const { investAccountId, platform_tier } = stateProps;
			const { submitAccount, submitAccountToApex, showDialog } = dispatchProps;
			const productTypes = platform_tier.product_types.reduce((acc, type) => {
				return { ...acc, [type]: true };
			}, {});

			// Create invest account and submit to APEX.
			if (!investAccountId && productTypes[PERSONAL_BROKERAGE]) {
				let investAccountResponse;

				try {
					investAccountResponse = await submitAccount({
						account: {
							account_type: PERSONAL_BROKERAGE,
							e_signature_agreed: true,
						},
					});
				} catch {
					await showDialog({
						prompt: contactUsMsg('Stash invest'),
						confirmText: 'Okay',
					});
				}

				try {
					await submitAccountToApex(investAccountResponse.account.id);
				} catch {
					await showDialog({
						prompt: contactUsMsg('invest'),
						confirmText: 'Okay',
					});
				}
			}

			ownProps.completeStep({ canReturn: false });
		},
	};
};

export class SignUpInvestEsig extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			ready: false,
			showAll: false,
			checked: true,
			isSubmitting: false,
		};
	}

	componentDidMount() {
		this.props.fetchMultiEsigAgreements([PERSONAL_BROKERAGE]).then(() => {
			this.setState({ ready: true });
		});
	}

	render() {
		const { agreementsData, onSubmit } = this.props;
		const { isSubmitting, checked } = this.state;

		const renderEsigAgreement = () => {
			return (
				<SignUpStep>
					<Box maxWidth="28rem" width="100%" pt="2rem">
						<CheckmarkIllustration
							alt="agreement"
							size="120px"
							display="block"
							mx="auto"
							mb="s"
						/>
						<Text.H1 size="3" mb="xxs">
							Review & accept your Stash agreements.
						</Text.H1>
						<Box className="signup__content">
							<Text mb="m">
								We believe in transparency. Within each section, you&apos;ll
								find simple summaries.
							</Text>
							<Box mb="s">
								<InfoToggle
									toggleText="Investing terms"
									align="left"
									fontWeight="bold"
									noPadding
								>
									<Flex alignItems="left" flexDirection="column">
										{agreementsData?.all_links?.map((document, idx) => (
											<ESigDocument
												key={idx}
												title={document.title}
												link={document.link}
												description={document.description}
											/>
										))}
									</Flex>
								</InfoToggle>
							</Box>
							<Flex alignItems="center" flexDirection="column">
								<PrimaryButton
									type="submit"
									mb="m"
									disabled={!checked}
									loading={isSubmitting}
									onClick={async () => {
										this.setState({ isSubmitting: true });
										await onSubmit();
										this.setState({ isSubmitting: false });
									}}
								>
									Accept & Continue
								</PrimaryButton>
							</Flex>
						</Box>
					</Box>
				</SignUpStep>
			);
		};

		return this.state.ready ? renderEsigAgreement() : <Loader center />;
	}
}

SignUpInvestEsig.propTypes = {
	agreementsData: PropTypes.object,
	platform_tier: PropTypes.object,
	onSubmit: PropTypes.func,
	fetchMultiEsigAgreements: PropTypes.func,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(SignUpInvestEsig);
